import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';

import { IconComponent } from '@mode/capra';
import dompurify from 'dompurify';
import { BehaviorSubject } from 'rxjs';

const DEFAULT_MAX_CHARS = 100;
@Component({
  standalone: true,
  selector: 'mode-in-place-input',
  imports: [CommonModule, FormsModule, IconComponent, MatInputModule],
  templateUrl: './in-place-input.component.html',
  styleUrls: ['./in-place-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InPlaceInputComponent implements AfterViewInit, OnChanges, OnDestroy {
  inputWidthInlineStyle$ = new BehaviorSubject<string>('100px');

  @ViewChild('inlineInputElement') inlineInputElement: ElementRef | undefined;

  @ViewChild('hiddenSpanForCalc', { static: true })
  hiddenSpanForCalc: ElementRef | undefined;

  @Input() text = '';
  @Input() readonly = false;
  @Input() maxchars = DEFAULT_MAX_CHARS;
  @Input() placeholder = 'Enter text';

  @Output() textChange = new EventEmitter<string>();

  hasFocus = false;
  hasError = false;
  private resizeObserver: ResizeObserver | undefined;

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => {
      this.adjustWidth();
    });
    if (this.hiddenSpanForCalc) {
      this.resizeObserver.observe(this.hiddenSpanForCalc.nativeElement);
    }
    this.adjustWidth();
  }

  ngOnChanges({ text }: SimpleChanges): void {
    if (text?.currentValue !== text.previousValue) {
      this.adjustWidth();
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  get placeholderText() {
    return !this.readonly ? this.placeholder : undefined;
  }

  get textOrPlaceholder() {
    return this.text === '' || this.text == null ? this.placeholder : this.text;
  }

  private stripAndDecode(text: string): string {
    const sanitized = dompurify.sanitize(text, { ALLOWED_TAGS: [] });
    return new DOMParser().parseFromString(sanitized, 'text/html').body.textContent ?? '';
  }

  onInput() {
    this.hasError = this.stripAndDecode(this.text) !== this.text;
    this.changeDetector.detectChanges();
  }

  emitTextChange() {
    if (this.hasError) {
      this.text = this.stripAndDecode(this.text);
      this.hasError = false;
      this.changeDetector.detectChanges();
    }
    this.textChange.emit(this.text);
  }

  activate() {
    if (this.readonly) {
      return;
    }
    this.hasFocus = true;
    this.inlineInputElement?.nativeElement?.focus();
    this.inlineInputElement?.nativeElement?.select();
    this.adjustWidth();
  }

  deactivate() {
    this.hasFocus = false;
    this.adjustWidth();
  }

  adjustWidth() {
    let styleWidth = '100%';
    if (!this.hasFocus) {
      const measureSpan: HTMLSpanElement = this.hiddenSpanForCalc?.nativeElement;
      if (measureSpan) {
        measureSpan.textContent = this.textOrPlaceholder;
        styleWidth = `${measureSpan.offsetWidth + 4}px`;
      }
    }
    this.inputWidthInlineStyle$.next(styleWidth);
    this.changeDetector.detectChanges();
  }
}
